import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useSelector } from '@xstate/react';
import { Link } from 'react-router';
import { Button, FormRow, Pagination, Pill, Select } from '~/components';
import { EmptyState } from '~/components/EmptyState';
import { marketSelectableCardClass, MarketStatusPill } from '~/features/shared/market-content';
import type { OptionType } from '~/features/types';
import { buildNewMsaasListingUrl } from '~/routing';
import type { ProviderCategory } from '~/types';
import { useMsaasListingSearchRef } from '../../marketSelectors';
import type { CompareListingOption } from '../api';
import { compareListingFor } from '../compareListings';
import { locationNote } from '../offeringFormat';
import { activeOfferingFilterLabels, OFFERING_SORT_OPTIONS } from '../offeringSearch';
import { useListingProviderCategories } from '../ProviderCategoriesContext';
import {
  selectFitQuotaActive,
  selectSearchFilters,
  selectSearchListing,
  selectSearchListings,
  selectSearchPagination,
} from '../selectors';
import type { Context, MsaasJobListingResponse } from '../types';
import { ListingCardFacts } from './ListingCardParts';
import { OfferingFitRow } from './OfferingFit';

const WarningFlag = ({ text }: { text: string }) => (
  <Pill
    type="warning"
    label={text}
    icon={<ExclamationTriangleIcon className="size-4 shrink-0" aria-hidden="true" />}
  />
);

const LocationLine = ({
  listing,
  buyerHasLocation,
}: {
  listing: MsaasJobListingResponse;
  buyerHasLocation: boolean;
}) => {
  const note = locationNote(listing, buyerHasLocation);
  if (note.kind === 'none') return null;
  return (
    <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-6 text-content-secondary">
      {note.kind === 'add-location' ? (
        <span>Add your location to see distance</span>
      ) : (
        <>
          {note.text && <span>{note.text}</span>}
          {note.flags.map((flag) => (
            <WarningFlag key={flag.key} text={flag.text} />
          ))}
        </>
      )}
    </p>
  );
};

/**
 * [MCV-1958] A Buying card wears the Selling card's chrome and body (`ListingCardFacts`). It holds
 * a "Check fit" button, and a button cannot sit inside the card-as-a-button the Selling list uses,
 * so the card is a plain container and the select control is a button stretched over it
 * (`absolute inset-0`, first in the tab order). The text above it lets clicks fall through; only
 * the Check fit button takes them, and `[title]` elements keep hover so the truncated compare
 * title still shows its tooltip.
 */
const OfferingRow = ({
  listing,
  index,
  selected,
  responded,
  buyerHasLocation,
  categories,
  compareListings,
  quotaActive,
  onSelect,
  onCheckFit,
}: {
  listing: MsaasJobListingResponse;
  index: number;
  selected: boolean;
  responded: boolean;
  buyerHasLocation: boolean;
  categories: ProviderCategory[];
  compareListings: CompareListingOption[];
  quotaActive: boolean;
  onSelect: () => void;
  onCheckFit: () => void;
}) => {
  const compare = compareListingFor(listing.fit?.compareListingId, compareListings);
  const cy = (field: string) => `offering-${field}-${index}`;
  const factsId = `offering-facts-${listing.id}`;
  const fitStatus = listing.fit?.status ?? 'NO_COMPARE_LISTING';
  return (
    <div
      className={marketSelectableCardClass(selected, 'relative mb-3')}
      data-cy={`offering-result-${index}`}
    >
      <button
        type="button"
        aria-pressed={selected}
        // The facts tell apart two sellers with the same title.
        aria-label={listing.listingTitle || `Listing ${listing.id}`}
        aria-describedby={factsId}
        className="absolute inset-0 size-full rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-50/20"
        onClick={onSelect}
      />
      {/* Keyboard selection is the stretched button above. A [title] element (the pills, the
          truncated compare title) keeps hover for its tooltip, so its click lands here instead of
          on that button and is routed to the same selection; buttons keep their own clicks. */}
      <div
        className="pointer-events-none relative [&_[title]]:pointer-events-auto [&_button]:pointer-events-auto"
        onClick={(event) => {
          if (!(event.target as Element).closest('button')) onSelect();
        }}
      >
        <div className="mb-2 space-y-1">
          <p className="text-lg font-bold leading-snug text-content-primary [overflow-wrap:anywhere]">
            {listing.listingTitle}
          </p>
          <div className="flex flex-wrap gap-2">
            {responded && <MarketStatusPill status="responded" label="Requested" />}
            {listing.isRenewable && <MarketStatusPill status="neutral" label="Renewable" />}
          </div>
        </div>
        {/* [MCV-1961] The card shows the Fit block only once a compare listing is picked; the
            detail panel still explains how. */}
        {fitStatus !== 'NO_COMPARE_LISTING' && (
          <div className="my-3 text-sm leading-6 text-content-secondary" data-cy={cy('fit')}>
            <p className="font-semibold">Fit</p>
            <OfferingFitRow
              align="left"
              listing={listing}
              compareTitle={compare?.listingTitle}
              quotaActive={quotaActive}
              onCheckFit={onCheckFit}
            />
          </div>
        )}
        <div id={factsId}>
          <ListingCardFacts
            listing={listing}
            categories={categories}
            cyTags={{
              states: cy('states'),
              ftes: cy('ftes'),
              dates: cy('dates'),
              workLocation: cy('work-location'),
              contractValue: cy('contract-value'),
            }}
            afterGrid={<LocationLine listing={listing} buyerHasLocation={buyerHasLocation} />}
            priceAside={
              (listing.rosterCount ?? 0) > 0 ? (
                <p
                  className="ml-auto text-sm font-normal leading-6 text-content-secondary"
                  data-cy={cy('roster')}
                >
                  Roster: {listing.rosterCount}
                </p>
              ) : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};

export const OfferingListingsList = ({
  compareListings,
  onClearFilters,
  onSelectListing,
}: {
  compareListings: CompareListingOption[];
  onClearFilters: () => void;
  /** Called after a row is picked; the narrow layout uses it to open the detail pane. */
  onSelectListing?: () => void;
}) => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const listings = useSelector(msaasListingsSearchRef, selectSearchListings);
  const currentListing = useSelector(msaasListingsSearchRef, selectSearchListing);
  const pagination = useSelector(msaasListingsSearchRef, selectSearchPagination);
  const filters = useSelector(msaasListingsSearchRef, selectSearchFilters);
  const quotaActive = useSelector(msaasListingsSearchRef, selectFitQuotaActive);
  const categories = useListingProviderCategories()?.categories ?? [];
  const employerId = useSelector(
    msaasListingsSearchRef,
    (state) => state?.context.employerUser?.employerId
  );
  if (!msaasListingsSearchRef || !pagination) return null;

  const activeFilters = activeOfferingFilterLabels(filters ?? {});

  return (
    <div className="px-4 pt-4 text-left">
      <div className="mb-4 border-b border-grey-900 pb-3 text-left">
        <h3 className="mb-3 w-full text-xl font-bold leading-snug text-content-primary">
          Labor available to hire
        </h3>
        <p className="text-sm font-normal leading-6 text-content-secondary">
          {pagination.total} {pagination.total === 1 ? 'seller' : 'sellers'} found
        </p>
        <FormRow className="mt-3 w-full">
          <Select
            name="sortBy"
            label="Sort By"
            cyTag="offering-sort-by"
            options={OFFERING_SORT_OPTIONS}
            onChange={(option) =>
              msaasListingsSearchRef.send({
                type: 'ON_SORT',
                payload: { sortBy: (option as OptionType).value as Context['sortBy'] },
              })
            }
          />
        </FormRow>
      </div>
      <div>
        {listings.length === 0 &&
          (activeFilters.length === 0 ? (
            <EmptyState
              cyTag="offering-empty-nothing-listed"
              title="No staffing groups are offering labor yet."
              description="Post what you need so sellers can find you."
              action={
                // Styled as the primary Button; a Button inside a Link would nest one control in another.
                <Link
                  to={buildNewMsaasListingUrl('buying')}
                  data-cy="offering-empty-post"
                  className="body-text-sb inline-flex h-[50px] min-w-[126px] items-center justify-center rounded-control bg-primary-50 p-4 text-white hover:bg-primary-100 active:bg-primary-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-50"
                >
                  Post what you need
                </Link>
              }
            />
          ) : (
            <EmptyState
              cyTag="offering-empty-filtered"
              title="No sellers match these filters"
              description={`Active filters: ${activeFilters.join(', ')}`}
              action={<Button type="button" label="Clear filters" onClick={onClearFilters} />}
            />
          ))}
        {listings.map((listing, index) => (
          <OfferingRow
            key={listing.id}
            listing={listing}
            index={index}
            selected={listing.id === currentListing?.id}
            responded={
              !!listing.jobResponses?.some(
                (response) => response.respondingEmployerId === employerId
              )
            }
            buyerHasLocation={filters?.location != null}
            categories={categories}
            compareListings={compareListings}
            quotaActive={quotaActive}
            onSelect={() => {
              msaasListingsSearchRef.send({
                type: 'ON_SELECT',
                payload: { listingId: listing.id },
              });
              onSelectListing?.();
            }}
            onCheckFit={() =>
              msaasListingsSearchRef.send({
                type: 'ON_CHECK_FIT',
                payload: { listingId: listing.id },
              })
            }
          />
        ))}
        <Pagination
          currentPage={pagination.page}
          totalPages={Math.ceil(pagination.total / pagination.pageSize)}
          onPageChange={(page) => msaasListingsSearchRef.send({ type: 'ON_PAGINATE', page })}
        />
      </div>
    </div>
  );
};
